import { useRef, useState } from 'react'
import { useEquipmentScope } from './equipmentScope'
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ATTACHMENT_ACCEPTED_MIME } from '@/types/cgt'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { formatBytes } from '@/components/renderer/AttachmentPreview'
import {
  checkDocumentFile,
  isValidDocumentName,
  suggestDocumentName,
} from '@/lib/equipmentDocuments'
import {
  openEquipmentDocument,
  useEquipmentTypeDocuments,
  useEquipmentUnitDocuments,
} from '@/hooks/useEquipmentDocuments'
import { EquipmentDocumentPreview } from './EquipmentDocumentPreview'
import type { EquipmentDocument } from '@/api/equipmentDocumentsApi'

/**
 * The documents filed against an equipment type or a unit (ClickUp z8nrz7cnj9).
 *
 * All logic lives in `@/lib/equipmentDocuments` — vitest here is
 * `environment: node` / `include: ['src/**\/*.test.ts']`, so a test for this
 * file would silently never run. Keep decisions out of the JSX.
 *
 * Write controls gate on `equipment.manage` — the permission the upload, rename
 * and delete routes actually carry — rather than on which screen this is. A
 * control that renders and then 403s on click is worse than one that isn't
 * there. `readOnly` is the separate axis: the info dialog shows documents to
 * someone who may well hold the permission, because that view is for reading.
 */
export function EquipmentDocumentsPanel({
  owner,
  readOnly = false,
}: {
  owner: { kind: 'type'; id: string } | { kind: 'unit'; id: string }
  readOnly?: boolean
}) {
  const { can } = usePermission()
  const canManage = !readOnly && can(PERMISSIONS.equipmentManage)

  return owner.kind === 'type' ? (
    <TypeDocuments id={owner.id} canManage={canManage} />
  ) : (
    <UnitDocuments id={owner.id} canManage={canManage} />
  )
}

function TypeDocuments({ id, canManage }: { id: string; canManage: boolean }) {
  const { list, upload, rename, remove } = useEquipmentTypeDocuments(id)
  return (
    <DocumentsSection
      documents={list.data ?? []}
      inherited={[]}
      isLoading={list.isLoading}
      error={list.error}
      canManage={canManage}
      upload={upload}
      rename={rename}
      remove={remove}
    />
  )
}

function UnitDocuments({ id, canManage }: { id: string; canManage: boolean }) {
  const { list, upload, rename, remove } = useEquipmentUnitDocuments(id)
  return (
    <DocumentsSection
      documents={list.data?.own ?? []}
      inherited={list.data?.inherited ?? []}
      isLoading={list.isLoading}
      error={list.error}
      canManage={canManage}
      upload={upload}
      rename={rename}
      remove={remove}
    />
  )
}

/**
 * The slice of a TanStack mutation these components use. Structural rather than
 * `UseMutationResult<…>` so the three call sites don't each have to name the
 * data/error/context generics they never touch.
 */
type Mutation<TVars> = {
  mutate: (vars: TVars, options?: { onSuccess?: () => void }) => void
  isPending: boolean
  error: unknown
  reset: () => void
}

function DocumentsSection({
  documents,
  inherited,
  isLoading,
  error,
  canManage,
  upload,
  rename,
  remove,
}: {
  documents: EquipmentDocument[]
  inherited: EquipmentDocument[]
  isLoading: boolean
  error: unknown
  canManage: boolean
  upload: Mutation<{ name: string; file: File }>
  rename: Mutation<{ id: string; name: string }>
  remove: Mutation<string>
}) {
  const { copy } = useEquipmentScope()
  const [adding, setAdding] = useState(false)

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <Label className='flex items-center gap-1.5'>
          <Paperclip className='h-3.5 w-3.5' />
          Documents
        </Label>
        {canManage && !adding && (
          <Button type='button' size='sm' variant='outline' onClick={() => setAdding(true)}>
            <Plus className='mr-1 h-3.5 w-3.5' />
            Add document
          </Button>
        )}
      </div>

      {adding && (
        <AddDocumentForm
          upload={upload}
          onDone={() => setAdding(false)}
          onCancel={() => setAdding(false)}
        />
      )}

      {isLoading && (
        <p className='text-xs text-muted-foreground'>Loading documents…</p>
      )}
      {!!error && (
        <p className='text-xs text-destructive'>
          {error instanceof Error ? error.message : 'Could not load documents.'}
        </p>
      )}

      {!isLoading && !error && documents.length === 0 && inherited.length === 0 && (
        <p className='text-xs text-muted-foreground'>
          No documents yet — manuals, calibration certificates and spec sheets go here.
        </p>
      )}

      {documents.length > 0 && (
        <ul className='divide-y rounded-md border'>
          {documents.map(doc => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              canManage={canManage}
              rename={rename}
              remove={remove}
            />
          ))}
        </ul>
      )}

      {inherited.length > 0 && (
        <div className='space-y-1 pt-1'>
          <p className='text-xs text-muted-foreground'>
            From the {copy.unitLower} type — edit these on the type itself.
          </p>
          <ul className='divide-y rounded-md border border-dashed'>
            {inherited.map(doc => (
              <DocumentRow key={doc.id} doc={doc} canManage={false} rename={rename} remove={remove} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function AddDocumentForm({
  upload,
  onDone,
  onCancel,
}: {
  upload: Mutation<{ name: string; file: File }>
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const pick = (picked: File | undefined) => {
    if (!picked) return
    const check = checkDocumentFile(picked)
    if (!check.ok) {
      setFile(null)
      setFileError(
        check.reason === 'type'
          ? 'Only JPEG, PNG and PDF files can be uploaded.'
          : `File is ${formatBytes(check.sizeBytes)} — the limit is ${formatBytes(check.limitBytes)}.`,
      )
      return
    }
    setFileError(null)
    setFile(picked)
    // Only fills an untouched field, so a name the user already typed survives
    // swapping the file.
    if (!name.trim()) setName(suggestDocumentName(picked.name))
  }

  const submit = () => {
    if (!file || !isValidDocumentName(name)) return
    // Per-call callback; the hook's own onSuccess (cache invalidation) still runs.
    upload.mutate({ name, file }, { onSuccess: onDone })
  }

  return (
    <div className='space-y-2 rounded-md border bg-muted/30 p-3'>
      <div className='space-y-1'>
        <Label htmlFor='equipment-document-name' className='text-xs'>
          Document name
        </Label>
        <Input
          id='equipment-document-name'
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder='e.g. Service manual'
        />
      </div>

      <div className='flex items-center gap-2'>
        <input
          ref={inputRef}
          type='file'
          className='hidden'
          accept={ATTACHMENT_ACCEPTED_MIME.join(',')}
          onChange={e => {
            pick(e.target.files?.[0])
            // Reset so re-picking the SAME file still fires a change event.
            e.target.value = ''
          }}
        />
        <Button type='button' size='sm' variant='outline' onClick={() => inputRef.current?.click()}>
          {file ? 'Change file' : 'Choose file'}
        </Button>
        <span className='truncate text-xs text-muted-foreground'>
          {file ? `${file.name} · ${formatBytes(file.size)}` : 'PDF, JPEG or PNG'}
        </span>
      </div>

      {fileError && <p className='text-xs text-destructive'>{fileError}</p>}
      {!!upload.error && (
        <p className='text-xs text-destructive'>
          {upload.error instanceof Error ? upload.error.message : 'Upload failed.'}
        </p>
      )}

      <div className='flex justify-end gap-2'>
        <Button
          type='button'
          size='sm'
          variant='ghost'
          onClick={() => {
            upload.reset()
            onCancel()
          }}
        >
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          disabled={!file || !isValidDocumentName(name) || upload.isPending}
          onClick={submit}
        >
          {upload.isPending && <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />}
          Upload
        </Button>
      </div>
    </div>
  )
}

function DocumentRow({
  doc,
  canManage,
  rename,
  remove,
}: {
  doc: EquipmentDocument
  canManage: boolean
  rename: Mutation<{ id: string; name: string }>
  remove: Mutation<string>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(doc.name)
  // Collapsed by default. EquipmentDocumentPreview fetches a presigned URL on
  // mount, so keeping it unmounted until asked is what stops a list of N
  // documents costing N requests every time the dialog opens.
  const [expanded, setExpanded] = useState(false)

  const save = () => {
    if (!isValidDocumentName(draft) || draft.trim() === doc.name) {
      setEditing(false)
      return
    }
    rename.mutate({ id: doc.id, name: draft })
    setEditing(false)
  }

  return (
    <li className='px-2 py-1.5 text-sm'>
      <div className='flex items-center gap-2'>
        <Button
          type='button'
          size='sm'
          variant='ghost'
          className='h-6 w-6 shrink-0 p-0'
          aria-expanded={expanded}
          title={expanded ? `Hide preview of ${doc.name}` : `Preview ${doc.name}`}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? (
            <ChevronDown className='h-4 w-4 text-muted-foreground' />
          ) : (
            <ChevronRight className='h-4 w-4 text-muted-foreground' />
          )}
        </Button>
        <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />

        {editing ? (
          <>
            <Input
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') save()
                if (e.key === 'Escape') {
                  setDraft(doc.name)
                  setEditing(false)
                }
              }}
              className='h-7'
            />
            <Button type='button' size='sm' variant='ghost' onClick={save}>
              Save
            </Button>
            <Button
              type='button'
              size='sm'
              variant='ghost'
              title='Cancel rename'
              onClick={() => {
                setDraft(doc.name)
                setEditing(false)
              }}
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          </>
        ) : (
          <>
            <div className='min-w-0 flex-1'>
              <p className='truncate font-medium'>{doc.name}</p>
              <p className='truncate text-xs text-muted-foreground'>
                {doc.fileName} · {formatBytes(doc.sizeBytes)}
              </p>
            </div>

            <Button
              type='button'
              size='sm'
              variant='ghost'
              title={`Download ${doc.name}`}
              onClick={() => void openEquipmentDocument(doc.id)}
            >
              <Download className='h-3.5 w-3.5' />
            </Button>

            {canManage && (
              <>
                <Button
                  type='button'
                  size='sm'
                  variant='ghost'
                  title={`Rename ${doc.name}`}
                  onClick={() => setEditing(true)}
                >
                  <Pencil className='h-3.5 w-3.5' />
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='ghost'
                  title={`Remove ${doc.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(doc.id)}
                >
                  <Trash2 className='h-3.5 w-3.5 text-destructive' />
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {expanded && (
        // Indented to line up under the name, so the preview reads as belonging
        // to this row rather than to the list.
        <div className='mt-2 pl-8'>
          <EquipmentDocumentPreview
            documentId={doc.id}
            fileName={doc.fileName}
            mimeType={doc.mimeType}
          />
        </div>
      )}
    </li>
  )
}
